import { Mail, MapPin, Linkedin, Twitter, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { ClinqLogo } from "../ClinqLogo";

const NAV_LINKS = [
  { label: "For Business", href: "/for-business" },
  { label: "For Projects", href: "/for-projects" },
  { label: "For Funders", href: "/for-funders" },
  { label: "For Advisors", href: "/for-advisors" },
  { label: "Get Funder-Ready", href: "/why-documents-matter" },
  { label: "FAQs", href: "/faq" },
];

const LEGAL_LINKS = [
  { label: "Privacy policy", to: "/privacy-policy" },
  { label: "Terms of service", to: "/terms" },
  { label: "NDA & Non-circumvent", to: "/nda" },
];

const COMPANY_LINKS = [
  { label: "Log in", to: "/login" },
  { label: "Sign up — Founder", to: "/join" },
  { label: "Sign up — Funder", to: "/get-started-funder" },
];

const SocialIconLink = ({ href, label, Icon, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    aria-label={label}
    data-testid={testid}
    className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:border-[#7BD3CE]/60 hover:text-[#7BD3CE]"
  >
    <Icon className="h-4 w-4" />
  </a>
);

export const Footer = () => {
  return (
    <footer data-testid="landing-footer" className="bg-black text-white">
      {/* Top — 4 columns */}
      <div className="mx-auto max-w-7xl px-6 pb-10 pt-16">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
          {/* Brand + contact */}
          <div className="col-span-2 md:col-span-1">
            <ClinqLogo size="lg" tone="teal" testid="footer-logo-mark" />
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-white/60">
              Linking capital to transformative African ventures.
            </p>
            <p data-testid="footer-tagline" className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-[#7BD3CE]">
              Unlock Your Capital Potential
            </p>

            <div className="mt-6 space-y-3 text-sm text-white/70">
              <a
                href="mailto:hello@clinq.africa"
                data-testid="footer-email"
                className="group inline-flex items-center gap-2 hover:text-[#7BD3CE]"
              >
                <Mail className="h-4 w-4 text-[#7BD3CE]" />
                <span className="underline-offset-4 group-hover:underline">
                  hello@clinq.africa
                </span>
              </a>
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#7BD3CE]" />
                <span className="text-white/60">
                  SNDBX International, CMS House,
                  <br />
                  850 Chania Ave, Hurlingham, Nairobi
                </span>
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              <SocialIconLink
                href="https://www.instagram.com/clinq.africa"
                label="Clinq on Instagram"
                Icon={Instagram}
                testid="footer-instagram"
              />
              <SocialIconLink
                href="https://www.linkedin.com/company/clinq-africa/"
                label="Clinq on LinkedIn"
                Icon={Linkedin}
                testid="footer-linkedin"
              />
              <SocialIconLink
                href="https://x.com/clinqafrica"
                label="Clinq on X / Twitter"
                Icon={Twitter}
                testid="footer-twitter"
              />
            </div>
          </div>

          {/* Platform */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
              Platform
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              {NAV_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="transition-colors hover:text-[#7BD3CE]"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
              Account
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              {COMPANY_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="transition-colors hover:text-[#7BD3CE]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/40">
              Legal
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-white/70">
              {LEGAL_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="transition-colors hover:text-[#7BD3CE]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Compliance band */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-6 py-5 text-[11px] text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Clinq. All rights reserved.
          </p>
          <p>
            Clinq is a technology platform. We do not provide investment, legal,
            or tax advice and are not a broker-dealer.
          </p>
        </div>
      </div>
    </footer>
  );
};
